import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MdCheckCircle, MdModeEdit, MdMoreHoriz } from 'react-icons/md'
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io'
import { RiRecordCircleLine } from 'react-icons/ri'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  useDeleteAccountMutation,
  useSetStatusMutation,
  useUpdateDisplayNameMutation,
} from '../../api/authApiSlice'
import {
  useLazyGetExtensionsQuery,
  useUpdateOrganizationMutation,
} from '../../api/extensionsApiSlice'
import { ERROR_SET_STATUS } from '../../constants/errorConstants'
import { CUSTOM_PRESENCE_TYP_MSGS } from '../../constants/matrixConst'
import { getPlanName } from '../../constants/planConstants'
import initMatrix from '../../lib/initMatrix'
import initTwilioVoice from '../../lib/initTwilioVoice'
import { logOut, selectCurrentUser, setUser } from '../../redux/reducers/authSlice'
import { setSelectedExtension } from '../../redux/reducers/extensionsSlice'
import { selectPresence, setPresence } from '../../redux/reducers/matrixSlice'
import { closeModal } from '../../redux/reducers/modalSlice'
import { store } from '../../redux/store'
import { localStorageService } from '../../services/localStorage'
import toastService from '../../services/toastService'
import { formatDate } from '../../utils/dateParsing'
import Button from '../Button/Button'
import Input from '../Input/Input'
import HelpTip from '../Help/HelpTip'
import styles from './Account.module.scss'

// Dialing 888 opens the voicemail greeting menu, same as the iOS overflow action
const GREETING_NUMBER = '888'

const PRESENCE_OPTIONS = [
  CUSTOM_PRESENCE_TYP_MSGS.online,
  CUSTOM_PRESENCE_TYP_MSGS.offline,
  CUSTOM_PRESENCE_TYP_MSGS.busy,
  CUSTOM_PRESENCE_TYP_MSGS.dnd,
]

const AccountInfoRow = ({ label, value, isEditable = false, onEdit }) => (
  <div className={styles.infoItem}>
    <span className={styles.infoLabel}>{label}</span>
    <div className={styles.infoValueWrapper}>
      <span className={styles.infoValue}>{value || '—'}</span>
      {isEditable && <MdModeEdit className={styles.editIcon} onClick={onEdit} />}
    </div>
  </div>
)

AccountInfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  isEditable: PropTypes.bool,
  onEdit: PropTypes.func,
}

const GroupRow = ({ group, selectedGroupId, onSelect, level = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const children = group.children || []
  const isSelected = selectedGroupId && selectedGroupId === group._id
  // Top level groups carry the organization number, children their own group number
  const groupNumber = group.parentId ? group.groupProviderNumber : group.organizationProviderNumber

  return (
    <div className={styles.groupRowWrapper}>
      <div className={styles.groupRow} style={{ paddingLeft: `${level * 1.5}rem` }}>
        <div
          className={`${styles.groupInfo} ${isSelected ? styles.groupInfoSelected : ''}`}
          onClick={() => onSelect(group)}
        >
          {isSelected ? (
            <MdCheckCircle className={styles.groupCheckSelected} />
          ) : (
            <RiRecordCircleLine className={styles.groupCheck} />
          )}
          <div className={styles.groupTexts}>
            <span className={styles.groupName}>{group.deviceName || ''}</span>
            {groupNumber ? (
              <span className={styles.groupDetail}>Group number: {groupNumber}</span>
            ) : null}
            {group.activePackage ? (
              <span className={styles.groupDetail}>
                Group plan: {getPlanName(group.activePackage)}
              </span>
            ) : null}
          </div>
        </div>
        {children.length > 0 && (
          <button
            type='button'
            className={styles.expandBtn}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>
        )}
      </div>
      {isExpanded &&
        children.map((child) => (
          <GroupRow
            key={child._id}
            group={child}
            selectedGroupId={selectedGroupId}
            onSelect={onSelect}
            level={level + 1}
          />
        ))}
    </div>
  )
}

GroupRow.propTypes = {
  group: PropTypes.object.isRequired,
  selectedGroupId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  level: PropTypes.number,
}

const Account = () => {
  const [displayName, setDisplayName] = useState('')
  const [showDisplayNameInput, setShowDisplayNameInput] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const menuRef = useRef(null)

  const [getExtensionQuery, { data, isLoading, error }] = useLazyGetExtensionsQuery()
  const [setStatusMutation] = useSetStatusMutation()
  const [updateDisplayName] = useUpdateDisplayNameMutation()
  const [updateOrganization] = useUpdateOrganizationMutation()
  const [deleteAccount] = useDeleteAccountMutation()

  const presence = useSelector(selectPresence)
  const user = useSelector(selectCurrentUser)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const extensions = useMemo(() => data?.extensions || [], [data])

  const selectedExtension = useMemo(
    () =>
      extensions.find(
        (extension) =>
          extension._id === user?.extensionId || extension.organizationId === user?.organizationId,
      ),
    [extensions, user?.extensionId, user?.organizationId],
  )

  // Extension numbers of the default extension (the one the 2 digit calls use)
  const defaultExtension = useMemo(
    () => extensions.find((extension) => extension.default),
    [extensions],
  )

  // Nest the flat list the backend returns, like the iOS Groups section
  const groupTree = useMemo(() => {
    const topLevel = extensions.filter((extension) => !extension.parentId)
    return topLevel.map((extension) => ({
      ...extension,
      children: extensions.filter(
        (child) => child.parentId && child.parentId === extension.organizationId,
      ),
    }))
  }, [extensions])

  useEffect(() => {
    dispatch(setSelectedExtension(selectedExtension))
  }, [dispatch, selectedExtension])

  useEffect(() => {
    if (user?._id) getExtensionQuery(user?._id)
  }, [getExtensionQuery, user?._id])

  const closeMenu = useCallback((e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false)
  }, [])

  useEffect(() => {
    if (!showMenu) return undefined
    document.addEventListener('mousedown', closeMenu)
    return () => document.removeEventListener('mousedown', closeMenu)
  }, [showMenu, closeMenu])

  const handleSelectGroup = async (extension) => {
    // Tapping the selected group clears the selection, as on iOS
    const extensionId = extension._id === selectedExtension?._id ? null : extension._id
    const response = await updateOrganization({ accountId: user?._id, extensionId })
    if (response?.error) {
      toastService.show('error', response.error?.data?.error?.message)
    } else {
      store.dispatch(setUser(response.data))
      getExtensionQuery(user?._id)
    }
  }

  const handleChangePresence = async (value) => {
    try {
      const response = await setStatusMutation({ accountId: user._id, status_msg: value })
      if (!response.error) {
        store.dispatch(setPresence(value))
        localStorageService.set('presence', value)
      } else {
        throw Error(ERROR_SET_STATUS)
      }
    } catch (err) {
      toastService.show('error', err.message)
    }
  }

  const displayNameHandler = async () => {
    try {
      if (displayName.trim() === '') {
        toastService.show('error', 'Display Name can not be empty')
        return
      }
      const response = await updateDisplayName({ displayName: displayName, accountId: user?._id })
      store.dispatch(setUser(response.data))
      setShowDisplayNameInput(false)
      setDisplayName('')
    } catch (err) {
      toastService.show('error', 'Something went wrong')
    }
  }

  const handleGreetingMessage = async () => {
    setShowMenu(false)
    try {
      const params = { To: GREETING_NUMBER }
      const { token } = await initTwilioVoice.getToken()
      if (token) await initTwilioVoice.updateToken(token)
      await initTwilioVoice.connectDevice(params)
      dispatch(closeModal())
      navigate('/call', { state: { params, name: 'Greeting message' } })
    } catch (err) {
      toastService.show('error', 'Not able to start the call')
    }
  }

  const handleDeleteAccount = async () => {
    const response = await deleteAccount(user?._id)
    if (response?.error) {
      toastService.show(
        'error',
        response.error?.data?.error?.message || 'Not able to delete account',
      )
      return
    }
    dispatch(closeModal())
    initMatrix.logout()
    initTwilioVoice.logOut()
    dispatch(logOut())
    navigate('/register')
  }

  if (!user) return null

  return (
    <div className={styles.accountWrapper}>
      <div className={styles.header}>
        <h3 className={styles.heading}>
          Account <HelpTip context='settings' />
        </h3>
        <div className={styles.menuWrapper} ref={menuRef}>
          <MdMoreHoriz className={styles.menuIcon} onClick={() => setShowMenu(!showMenu)} />
          {showMenu && (
            <div className={styles.menu}>
              <div className={styles.menuItem} onClick={handleGreetingMessage}>
                Change your greeting message
              </div>
              <div
                className={`${styles.menuItem} ${styles.menuItemDanger}`}
                onClick={() => {
                  setShowMenu(false)
                  setShowDeleteConfirmation(true)
                }}
              >
                Delete Account
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.userInfoWrapper}>
        <AccountInfoRow label='Username:' value={user?.number || user?.email} />
        <AccountInfoRow
          label='Display name:'
          value={user?.displayName}
          isEditable
          onEdit={() => setShowDisplayNameInput(!showDisplayNameInput)}
        />
        <AccountInfoRow label='AI Mobility number:' value={user?.twilioNumber} />
        {defaultExtension?.extensionNumber ? (
          <AccountInfoRow label='My extension:' value={defaultExtension.extensionNumber} />
        ) : null}
        {defaultExtension?.organizationExtensionNumber ? (
          <AccountInfoRow
            label='Organization extension:'
            value={defaultExtension.organizationExtensionNumber}
          />
        ) : null}
        {defaultExtension?.groupExtensionNumber ? (
          <AccountInfoRow label='Group extension:' value={defaultExtension.groupExtensionNumber} />
        ) : null}
        <AccountInfoRow
          label='Registered:'
          value={user?.createdAt ? formatDate(user?.createdAt, 'MMM DD, YYYY HH:mm:ss') : ''}
        />
        <AccountInfoRow
          label='Current plan:'
          value={getPlanName(selectedExtension?.activePackage)}
        />
      </div>

      {showDisplayNameInput && (
        <div className={styles.displayNameWrapper}>
          <Input
            placeholder='Enter Display Name'
            className={styles.displayName}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <div className={styles.displayNameBtn}>
            <Button onClick={displayNameHandler} label='Update Display Name' />
          </div>
        </div>
      )}

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>Groups:</h4>
        <div className={styles.groupWrapper}>
          {error ? (
            <div className={styles.warning}>Not able to fetch groups</div>
          ) : isLoading ? (
            <div className={styles.emptyGroups}>Loading groups…</div>
          ) : groupTree.length ? (
            groupTree.map((group) => (
              <GroupRow
                key={group._id}
                group={group}
                selectedGroupId={selectedExtension?._id}
                onSelect={handleSelectGroup}
              />
            ))
          ) : (
            <div className={styles.emptyGroups}>There is no groups!</div>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>Presence:</h4>
        <div className={styles.segmented}>
          {PRESENCE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type='button'
              className={`${styles.segment} ${
                presence === option.value ? styles.segmentActive : ''
              }`}
              onClick={() => handleChangePresence(option.value)}
            >
              {option.name}
            </button>
          ))}
        </div>
      </div>

      {showDeleteConfirmation && (
        <div className={styles.confirmWrapper}>
          <p className={styles.confirmTitle}>Delete Account</p>
          <p className={styles.confirmText}>
            Are you sure you want to delete your account?
            <br />
            This action cannot be undone.
          </p>
          <div className={styles.confirmActions}>
            <Button label='Cancel' onClick={() => setShowDeleteConfirmation(false)} />
            <Button label='Delete' className={styles.deleteBtn} onClick={handleDeleteAccount} />
          </div>
        </div>
      )}
    </div>
  )
}

export default Account
